/**
 * Login Page Component
 * Handles user authentication
 */

import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiService } from '../services/api';
import { API_CONFIG, API_ENDPOINTS } from '../config/apiConfig';
import './Auth.css';
import BrandLogo from '../assets/images/big-atom-logo copy.png';

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      console.log('🔐 Attempting login:', { email: formData.email });
      
      const trimmedEmail = formData.email.trim();
      const response = await apiService.post(API_ENDPOINTS.LOGIN, {
        email: trimmedEmail,
        password: formData.password,
      });

      // Store token if provided
      if (response.token || response.accessToken) {
        localStorage.setItem('auth_token', response.token || response.accessToken);
      }

      const resolvedEmail = response.email || trimmedEmail;
      const resolvedRole = response.role || 'ADMIN';
      const resolvedOrgId = response.orgId;
      const resolvedOrgNameRaw =
        response.orgName ||
        response.organizationName ||
        (response.org && (response.org.name || response.org.orgName)) ||
        '';
      const resolvedOrgName =
        typeof resolvedOrgNameRaw === 'string' ? resolvedOrgNameRaw.trim() : '';

      try {
        localStorage.setItem(
          'user_profile',
          JSON.stringify({
            email: resolvedEmail,
            role: resolvedRole,
            orgId: resolvedOrgId ?? null,
            orgName: resolvedOrgName,
          })
        );
      } catch (storageError) {
        console.warn('Unable to persist user profile to storage:', storageError);
      }

      console.log('✅ Login successful');
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        'Invalid email or password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-layout">
      <aside className="auth-brand">
        <div className="auth-brand-content">
          <img src={BrandLogo} alt="Big Atom" className="auth-brand-logo" />
          <h1 className="auth-brand-title">Log in to your quality command center</h1>
          <p className="auth-brand-subtitle">
            Track EMQ, coverage, and Conversions API health in one live workspace built for modern growth teams.
          </p>
          <ul className="auth-usp-list">
            <li>Realtime diagnostics for every event stream</li>
            <li>Smart alerts when EMQ or coverage drifts</li>
            <li>One-click drills into pixel-level detail</li>
          </ul>
        </div>
      </aside>

      <main className="auth-panel">
        <div className="auth-panel-card">
          <div className="auth-panel-header">
            <h2 className="auth-panel-title">Welcome back</h2>
            <p className="auth-panel-subtitle">Sign in with your Big Atom credentials</p>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            {error && (
              <div className="auth-error">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="you@company.com"
                required
                autoComplete="email"
              />
            </div>

            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Enter your password"
                required
                autoComplete="current-password"
              />
            </div>

            <button
              type="submit"
              className="auth-button auth-button-primary"
              disabled={loading}
            >
              {loading ? 'Signing you in…' : 'Sign In'}
            </button>
          </form>

          <div className="auth-panel-footer">
            <span>Don't have an account?</span>{' '}
            <Link to="/signup" className="auth-link">
              Create one
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Login;

